import React, { FocusEvent, useCallback } from 'react';
import componentProps, { ComponentProps } from '../../../../helpers/componentProps';
import { useFieldsetFocusEvents } from '../../../../hooks/useFieldsetFocusEvents';
import { applyComponentTheme } from '../../../../theme';
import { Grid } from '../../../Grid';
import { defaultLabelId, joinIds } from '../../idHelpers';
import { FieldSetInputProps, InputComponent } from '../../inputTypes';
import {
  DayOrMonthInput,
  GridItemFlex,
  HiddenLabel,
  InputWrapper,
  YearInput,
} from '../styles';

export type DateValue = {
  day?: string;
  month?: string;
  year?: string;
};

export type DateInputProps = {
  id: string;
  onChange: (date: DateValue) => void;
  value?: DateValue;
  hideDay?: boolean;
  onFocus?: (event: FocusEvent<HTMLDivElement>) => void;
  onBlur?: (event: FocusEvent<HTMLDivElement>) => void;
  disabled?: boolean;
} & FieldSetInputProps &
  ComponentProps;

const DateInput: InputComponent<DateInputProps> = ({
  onChange,
  value = {},
  hideDay,
  id,
  onFocus: externalOnFocus,
  onBlur: externalOnBlur,
  disabled,
  labelId = defaultLabelId(id),
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  ...rest
}) => {
  const dayInputId = `${id}-day`;
  const monthInputId = `${id}-month`;
  const yearInputId = `${id}-year`;

  const updateDate = useCallback(
    (type: keyof DateValue, newValue: string) => {
      const newInputDate = { ...value, [type]: newValue };
      onChange(newInputDate);

      switch (type) {
        case 'day':
          if (newValue.length === 2) {
            const monthInput = document.getElementById(monthInputId);
            monthInput?.focus();
          }
          break;
        case 'month':
          if (newValue.length === 2) {
            const yearInput = document.getElementById(yearInputId);
            yearInput?.focus();
          }
          break;
        default:
      }
    },
    [onChange, value, monthInputId, yearInputId]
  );

  const { onBlur, onFocus } = useFieldsetFocusEvents({
    onBlur: externalOnBlur,
    onFocus: externalOnFocus,
  });

  return (
    <InputWrapper {...componentProps(rest)} id={id} onFocus={onFocus} onBlur={onBlur}>
      <Grid alignLeft>
        <GridItemFlex desktop={4} tabletLandscape={4} tabletPortrait={4}>
          {!hideDay && (
            <>
              <DayOrMonthInput
                id={dayInputId}
                name="Day"
                onChange={(input) => updateDate('day', input.target.value)}
                numbersOnly
                maxLength={2}
                placeholder="DD"
                value={value.day ?? ''}
                disabled={disabled}
                aria-labelledby={joinIds(labelId, defaultLabelId(dayInputId))}
                aria-placeholder="Day"
                aria-describedby={ariaDescribedBy}
                aria-invalid={ariaInvalid}
              />
              <HiddenLabel id={defaultLabelId(dayInputId)} htmlFor={dayInputId}>
                Day
              </HiddenLabel>
            </>
          )}
          <DayOrMonthInput
            id={monthInputId}
            name="Month"
            onChange={(input) => updateDate('month', input.target.value)}
            numbersOnly
            maxLength={2}
            placeholder="MM"
            value={value.month ?? ''}
            disabled={disabled}
            aria-labelledby={joinIds(labelId, defaultLabelId(monthInputId))}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
          />
          <HiddenLabel id={defaultLabelId(monthInputId)} htmlFor={monthInputId}>
            Month
          </HiddenLabel>
          <YearInput
            id={yearInputId}
            name="Year"
            onChange={(input) => updateDate('year', input.target.value)}
            numbersOnly
            maxLength={4}
            placeholder="YYYY"
            value={value.year ?? ''}
            disabled={disabled}
            aria-labelledby={joinIds(labelId, defaultLabelId(yearInputId))}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
          />
          <HiddenLabel id={defaultLabelId(yearInputId)} htmlFor={yearInputId}>
            Year
          </HiddenLabel>
        </GridItemFlex>
      </Grid>
    </InputWrapper>
  );
};

DateInput.isFieldSet = true;

export default applyComponentTheme(DateInput, 'DateInput');
